import { Component } from '@theme/component';
const key = 'aren-discovery-v1';
const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,200}$/.test(value);
const read = () => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || '{}');
    return { recent: Array.isArray(value.recent) ? value.recent.filter(validHandle).slice(0, 12) : [], saved: Array.isArray(value.saved) ? value.saved.filter(validHandle).slice(0, 24) : [] };
  } catch { return { recent: [], saved: [] }; }
};
const write = value => { try { localStorage.setItem(key, JSON.stringify(value)); } catch {} };
const synchronize = () => {
  const saved = read().saved;
  for (const button of document.querySelectorAll('[data-aren-save]')) {
    button.hidden = false;
    button.setAttribute('aria-pressed', String(saved.includes(button.dataset.arenSave)));
  }
};
document.addEventListener('click', event => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('[data-aren-save]');
  if (!(button instanceof HTMLButtonElement) || !validHandle(button.dataset.arenSave)) return;
  event.preventDefault();
  const state = read();
  const handle = button.dataset.arenSave;
  state.saved = state.saved.includes(handle) ? state.saved.filter(value => value !== handle) : [handle, ...state.saved].slice(0, 24);
  write(state);
  synchronize();
  document.dispatchEvent(new CustomEvent('aren:saved'));
});
document.addEventListener('shopify:section:load', synchronize);
window.addEventListener('storage', synchronize);

class ArenDiscovery extends Component {
  #controller;
  #observer;
  #cache = new Map();
  #renderId = 0;
  connectedCallback() { super.connectedCallback(); this.#connect(); }
  updatedCallback() { super.updatedCallback(); this.#cleanup(); this.#connect(); }
  disconnectedCallback() { this.#cleanup(); super.disconnectedCallback(); }
  #cleanup() { this.#controller?.abort(); this.#observer?.disconnect(); this.#renderId++; }
  #connect() {
    this.#controller = new AbortController();
    const state = read();
    const handle = this.dataset.currentHandle;
    if (validHandle(handle)) { state.recent = [handle, ...state.recent.filter(value => value !== handle)].slice(0, 12); write(state); }
    synchronize();
    document.addEventListener('aren:saved', this.#render, { signal: this.#controller.signal });
    if (!state.recent.length && !state.saved.length) return;
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { this.#observer.disconnect(); this.#render(); } }, { rootMargin: '250px' });
      this.#observer.observe(this);
    } else this.#render();
  }
  #getProduct = async handle => {
    if (this.#cache.has(handle)) return this.#cache.get(handle);
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { signal: this.#controller.signal });
      if (!response.ok) return null;
      const product = await response.json();
      if (product.handle !== handle || !product.title) return null;
      this.#cache.set(handle, product);
      return product;
    } catch { return null; }
  };
  #render = async () => {
    const id = ++this.#renderId;
    const state = read();
    for (const [type, handles] of [['recent', state.recent.filter(value => value !== this.dataset.currentHandle).slice(0, 4)], ['saved', state.saved.slice(0, 8)]]) {
      const section = this.querySelector(`[data-aren-${type}]`);
      const grid = this.querySelector(`[data-aren-${type}-grid]`);
      if (!section || !grid) continue;
      const products = (await Promise.all(handles.map(this.#getProduct))).filter(Boolean);
      if (id !== this.#renderId || !this.isConnected) return;
      grid.replaceChildren(...products.map(product => this.#card(product, type)));
      section.hidden = !products.length;
    }
    synchronize();
  };
  #card(product, type) {
    const article = document.createElement('article');
    article.className = 'aren-discovery-card';
    const link = document.createElement('a');
    link.href = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`;
    if (product.featured_image) {
      const image = document.createElement('img');
      try {
        const url = new URL(product.featured_image, location.origin);
        if (url.origin !== location.origin && !url.hostname.endsWith('shopify.com')) throw new Error('Unsupported image host');
        url.searchParams.set('width', '600');
        image.src = url.href;
        image.srcset = [240, 400, 600].map(width => {
          const candidate = new URL(url.href);
          candidate.searchParams.set('width', String(width));
          return `${candidate.href} ${width}w`;
        }).join(', ');
        image.sizes = '(max-width: 749px) calc((100vw - 48px) / 2), (min-width: 1560px) 358px, calc((100vw - 128px) / 4)';
        image.alt = product.title;
        image.loading = 'lazy';
        image.width = 480;
        image.height = 600;
        link.append(image);
      } catch {}
    }
    const name = document.createElement('h3');
    name.textContent = product.title;
    const price = document.createElement('p');
    price.textContent = new Intl.NumberFormat(this.dataset.locale || 'pt-BR', { style: 'currency', currency: this.dataset.currency || 'BRL' }).format(Number(product.price) / 100);
    link.append(name, price);
    article.append(link);
    if (type === 'saved') {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.arenSave = product.handle;
      button.textContent = this.dataset.removeLabel;
      button.setAttribute('aria-pressed', 'true');
      article.append(button);
    }
    return article;
  }
}
if (!customElements.get('aren-discovery')) customElements.define('aren-discovery', ArenDiscovery);
